/**
 * Menu Tank Consume Animation — spawns tanks near the void that fall in,
 * triggering real consume effects on contact.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { WORLD, Z, VOID, WALL_BOUNCE } from '../../constants';
import type { GameState } from '../../types';
import type { MenuConsumeAnim, MenuAnimDeps, VoidTankDef } from './types';
import { TANK_ROSTER, MENU_MAX_SPEED, rollTankCount } from './types';

export class MenuTankConsumeAnimation {
    anim: MenuConsumeAnim;
    private worldContainer: Container;

    constructor(worldContainer: Container) {
        this.worldContainer = worldContainer;
        this.anim = {
            phase: 'idle',
            idleTimer: 4 + Math.random() * 5,
            tanks: [],
            consumedTexts: [],
        };
    }

    update(dt: number, state: GameState, deps: MenuAnimDeps): void {
        const anim = this.anim;

        // --- Always update consumed texts (independent lifecycle) ---
        for (let i = anim.consumedTexts.length - 1; i >= 0; i--) {
            const ct = anim.consumedTexts[i];
            ct.life -= dt;
            const t = Math.max(0, ct.life / ct.maxLife);
            ct.text.position.y = ct.startY - (1 - t) * 80;
            ct.text.alpha = t * t;
            const scale = 0.7 + 0.3 * t;
            ct.text.scale.set(scale, scale);

            if (ct.life <= 0) {
                this.worldContainer.removeChild(ct.text);
                ct.text.destroy();
                anim.consumedTexts.splice(i, 1);
            }
        }

        switch (anim.phase) {
            case 'idle': {
                anim.idleTimer -= dt;
                if (anim.idleTimer <= 0) {
                    const count = rollTankCount();

                    for (let n = 0; n < count; n++) {
                        const rosterIdx = Math.floor(Math.random() * TANK_ROSTER.length);
                        const tank = TANK_ROSTER[rosterIdx];

                        const baseDmg = tank.damagePercent;
                        const damage = Math.max(0, baseDmg + (Math.random() - 0.5) * 60);

                        const centerX = WORLD.VIRTUAL_WIDTH / 2;
                        const centerRegion = WORLD.VIRTUAL_WIDTH * 0.3;
                        const safeY = state.voidY - 250;

                        const forceDir = count > 1 ? (n % 2 === 0 ? -1 : 1) : 0;
                        const angleSpread = count > 1 ? ((n / count) - 0.5) * 1.6 : 0;

                        let tx: number, ty: number, tvx: number, tvy: number;
                        const spawnStyle = Math.random();

                        if (spawnStyle < 0.4) {
                            tx = centerX + (Math.random() - 0.5) * centerRegion;
                            ty = safeY - Math.random() * 150;
                            const baseAngle = -Math.PI / 2 + angleSpread;
                            const launchAngle = baseAngle + (Math.random() - 0.5) * 0.8;
                            const speed = 550 + Math.random() * 500;
                            tvx = Math.cos(launchAngle) * speed;
                            tvy = Math.sin(launchAngle) * speed;
                            if (forceDir !== 0 && Math.sign(tvx) !== forceDir) tvx = -tvx;
                        } else if (spawnStyle < 0.7) {
                            tx = centerX + (Math.random() - 0.5) * centerRegion * 0.5;
                            ty = Math.min(safeY, state.voidY - 300) - Math.random() * 200;
                            const goRight = forceDir !== 0 ? forceDir > 0 : Math.random() < 0.5;
                            const angle = goRight
                                ? -0.5 - Math.random() * 0.6
                                : Math.PI + 0.5 + Math.random() * 0.6;
                            const speed = 600 + Math.random() * 400;
                            tvx = Math.cos(angle) * speed;
                            tvy = Math.sin(angle) * speed;
                        } else {
                            tx = centerX + (Math.random() - 0.5) * centerRegion * 0.3;
                            ty = Math.min(safeY, state.voidY - 300) - Math.random() * 200;
                            const hDir = forceDir !== 0 ? forceDir : (Math.random() < 0.5 ? 1 : -1);
                            tvx = hDir * (400 + Math.random() * 500);
                            tvy = -300 - Math.random() * 400;
                        }

                        if (count > 1) {
                            tx += (n - count / 2) * 40;
                            ty -= n * 20;
                        }

                        const launchSpeed = Math.sqrt(tvx * tvx + tvy * tvy);
                        const spinRate = (3 + launchSpeed / 120) * (Math.random() < 0.5 ? 1 : -1);

                        const gfx = new Graphics();
                        gfx.zIndex = Z.TANKS;
                        this.drawTankShape(gfx, 0, 0, 18, tank.color, tank.shape);
                        gfx.position.set(tx, ty);
                        this.worldContainer.addChild(gfx);

                        anim.tanks.push({
                            rosterIndex: rosterIdx,
                            gfx,
                            x: tx, y: ty,
                            vx: tvx, vy: tvy,
                            rotation: Math.random() * Math.PI * 2,
                            spinRate,
                            damage,
                        });
                    }

                    anim.phase = 'active';
                }
                break;
            }

            case 'active': {
                for (let i = anim.tanks.length - 1; i >= 0; i--) {
                    const ft = anim.tanks[i];
                    const tank = TANK_ROSTER[ft.rosterIndex];

                    ft.vy += 400 * dt;
                    ft.x += ft.vx * dt;
                    ft.y += ft.vy * dt;
                    ft.rotation += ft.spinRate * dt;

                    const tankRadius = 18;
                    const leftBound = WORLD.WORLD_LEFT + tankRadius;
                    const rightBound = WORLD.WORLD_RIGHT - tankRadius;
                    const damageMult = Math.min(
                        1 + ft.damage / WALL_BOUNCE.DAMAGE_SCALE,
                        WALL_BOUNCE.MAX_BOUNCE_MULT,
                    );
                    const bounceRetention = WALL_BOUNCE.BASE_RETENTION * damageMult;

                    let bounced = false;
                    let bounceX = 0;
                    let bounceDir = 0;
                    let isSpike = false;

                    if (ft.x <= leftBound && ft.vx < 0) {
                        ft.x = leftBound;
                        const preBounceSpeed = Math.sqrt(ft.vx * ft.vx + ft.vy * ft.vy);
                        ft.vx = -ft.vx * bounceRetention;
                        const spikeRatio = preBounceSpeed > 0.1 ? Math.abs(ft.vy) / preBounceSpeed : 0;
                        isSpike = spikeRatio > WALL_BOUNCE.SPIKE_ANGLE_THRESHOLD && ft.vy > 0;
                        if (isSpike) {
                            ft.vy *= WALL_BOUNCE.SPIKE_VY_RETENTION;
                        } else {
                            ft.vy *= WALL_BOUNCE.NON_SPIKE_VY_DAMPEN;
                            ft.vy += WALL_BOUNCE.POP_VY * 60;
                        }
                        ft.spinRate = -ft.spinRate * 1.2;
                        bounced = true; bounceX = leftBound; bounceDir = 1;
                    } else if (ft.x >= rightBound && ft.vx > 0) {
                        ft.x = rightBound;
                        const preBounceSpeed = Math.sqrt(ft.vx * ft.vx + ft.vy * ft.vy);
                        ft.vx = -ft.vx * bounceRetention;
                        const spikeRatio = preBounceSpeed > 0.1 ? Math.abs(ft.vy) / preBounceSpeed : 0;
                        isSpike = spikeRatio > WALL_BOUNCE.SPIKE_ANGLE_THRESHOLD && ft.vy > 0;
                        if (isSpike) {
                            ft.vy *= WALL_BOUNCE.SPIKE_VY_RETENTION;
                        } else {
                            ft.vy *= WALL_BOUNCE.NON_SPIKE_VY_DAMPEN;
                            ft.vy += WALL_BOUNCE.POP_VY * 60;
                        }
                        ft.spinRate = -ft.spinRate * 1.2;
                        bounced = true; bounceX = rightBound; bounceDir = -1;
                    }

                    const speed = Math.sqrt(ft.vx * ft.vx + ft.vy * ft.vy);
                    if (speed > MENU_MAX_SPEED) {
                        const ratio = MENU_MAX_SPEED / speed;
                        ft.vx *= ratio;
                        ft.vy *= ratio;
                    }

                    if (bounced) {
                        const postSpeed = Math.sqrt(ft.vx * ft.vx + ft.vy * ft.vy);
                        const intensity = Math.min(1, postSpeed / 500);
                        deps.gameParticles.spawnWallBounce(
                            bounceX, ft.y, bounceDir, tank.color, intensity, isSpike,
                        );
                        const shakeBase = isSpike
                            ? WALL_BOUNCE.SPIKE_SHAKE_INTENSITY
                            : WALL_BOUNCE.SHAKE_INTENSITY;
                        const shakeDecay = isSpike
                            ? WALL_BOUNCE.SPIKE_SHAKE_DECAY
                            : WALL_BOUNCE.SHAKE_DECAY;
                        deps.screenShake.trigger(shakeBase * intensity * 0.2, shakeDecay);
                    }

                    ft.gfx.position.set(ft.x, ft.y);
                    ft.gfx.rotation = ft.rotation;

                    if (ft.y >= state.voidY - 5) {
                        this.worldContainer.removeChild(ft.gfx);
                        ft.gfx.destroy();

                        deps.gameParticles.spawnVoidDeathExplosion(
                            ft.x, state.voidY, tank.color, state.voidY,
                        );
                        deps.voidRenderer.triggerEdgeDistortion(ft.x, state.voidY);
                        deps.voidRenderer.triggerConsumeBurst(ft.x, state.voidY);
                        deps.effects.triggerMultiFlash(VOID.EDGE_COLOR, VOID.FLASH_ALPHA * 0.3);
                        deps.camera.shake(VOID.CONSUME_SHAKE * 0.15, { decay: 0.88 });
                        deps.screenShake.trigger(VOID.CONSUME_SCREEN_SHAKE * 0.15, 0.88);

                        const consumedText = new Text({
                            text: 'CONSUMED',
                            style: new TextStyle({
                                fontFamily: '"JetBrains Mono", monospace',
                                fontSize: 28,
                                fontWeight: 'bold',
                                fill: VOID.EDGE_COLOR,
                                align: 'center',
                                stroke: { color: 0x000000, width: 3 },
                            }),
                        });
                        consumedText.anchor.set(0.5, 0.5);
                        consumedText.position.set(ft.x, state.voidY - 40);
                        consumedText.zIndex = Z.TANKS + 5;
                        this.worldContainer.addChild(consumedText);
                        anim.consumedTexts.push({
                            text: consumedText,
                            startY: state.voidY - 40,
                            life: 1.5,
                            maxLife: 1.5,
                        });

                        anim.tanks.splice(i, 1);
                    }
                }

                if (anim.tanks.length === 0 && anim.consumedTexts.length === 0) {
                    anim.idleTimer = 4 + Math.random() * 5;
                    anim.phase = 'idle';
                }
                break;
            }
        }
    }

    /** Clean up all tank consume animation elements (when leaving menu). */
    cleanup(): void {
        const anim = this.anim;
        for (const ft of anim.tanks) {
            this.worldContainer.removeChild(ft.gfx);
            ft.gfx.destroy();
        }
        anim.tanks = [];
        for (const ct of anim.consumedTexts) {
            this.worldContainer.removeChild(ct.text);
            ct.text.destroy();
        }
        anim.consumedTexts = [];
        anim.phase = 'idle';
        anim.idleTimer = 4 + Math.random() * 5;
    }

    /** Draw a tank shape for the falling tank animation. */
    private drawTankShape(gfx: Graphics, cx: number, cy: number, radius: number, color: number, shape: VoidTankDef['shape']): void {
        switch (shape) {
            case 'circle':
                gfx.circle(cx, cy, radius);
                gfx.fill({ color });
                break;
            case 'square': {
                const half = radius * 0.8;
                gfx.rect(cx - half, cy - half, half * 2, half * 2);
                gfx.fill({ color });
                break;
            }
            case 'triangle': {
                const pts: number[] = [];
                for (let i = 0; i < 3; i++) {
                    const a = (i / 3) * Math.PI * 2 - Math.PI / 2;
                    pts.push(cx + Math.cos(a) * radius, cy + Math.sin(a) * radius);
                }
                gfx.poly(pts);
                gfx.fill({ color });
                break;
            }
            case 'hexagon': {
                const pts: number[] = [];
                for (let i = 0; i < 6; i++) {
                    const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
                    pts.push(cx + Math.cos(a) * radius, cy + Math.sin(a) * radius);
                }
                gfx.poly(pts);
                gfx.fill({ color });
                break;
            }
            case 'diamond': {
                const pts = [
                    cx, cy - radius,
                    cx + radius, cy,
                    cx, cy + radius,
                    cx - radius, cy,
                ];
                gfx.poly(pts);
                gfx.fill({ color });
                break;
            }
            case 'star': {
                const pts: number[] = [];
                for (let i = 0; i < 10; i++) {
                    const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
                    const r = i % 2 === 0 ? radius : radius * 0.5;
                    pts.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
                }
                gfx.poly(pts);
                gfx.fill({ color });
                break;
            }
            case 'rectangle': {
                const hw = radius * 0.5;
                const hh = radius;
                gfx.rect(cx - hw, cy - hh, hw * 2, hh * 2);
                gfx.fill({ color });
                break;
            }
        }
        const turretLen = radius * 1.2;
        const turretW = radius * 0.3;
        gfx.rect(cx, cy - turretW / 2, turretLen, turretW);
        gfx.fill({ color });
    }
}
